import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  addToast,
  cn,
} from "@heroui/react";
import {
  CalendarCheck,
  CalendarRange,
  CalendarX,
  Clock,
  Coffee,
  Info,
  RotateCcw,
  Tag,
  TreePalm,
} from "lucide-react";
import type { ReactNode } from "react";
import { DurationInput } from "@/Components/NextBase/DurationInput";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { LimeColorPicker } from "@/Components/NextBase/LimeColorPicker";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeRadioGroup } from "@/Components/NextBase/LimeRadioGroup";
import { useConfirmModal } from "@/Components/NextBase/useConfirmModal";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { api } from "@/lib/api-client";
import type { GetTimeOffTypes, PostTimeOffType } from "@/server-types";
import { EmojiPickerGrid } from "./EmojiPickerGrid";
import { TimeOffTypeCompensationSection } from "./TimeOffTypeCompensationSection";
import { fallbackTimeOffEmoji } from "./timeOffEmojis";
import { resolveDefaultTimeOffTypeName } from "./timeOffTypeDisplayName";

type TimeOffTypeRow = GetTimeOffTypes["response"][number];

type Form = {
  name: string;
  color: string | null;
  emoji: string | null;
  consumesShift: boolean;
  granularity: PostTimeOffType["body"]["granularity"];
  hasDefaultDuration: boolean;
  defaultDurationMins: number;
  hasDailyLimit: boolean;
  maxDailyMinutes: number;
  blocksOnlineBooking: boolean;
  showOnPublicForm: boolean;
};

function defaultValues(granularity: Form["granularity"]): Form {
  return {
    name: "",
    color: null,
    emoji: fallbackTimeOffEmoji(granularity),
    consumesShift: true,
    granularity,
    hasDefaultDuration: false,
    defaultDurationMins: 30,
    hasDailyLimit: false,
    maxDailyMinutes: 60,
    blocksOnlineBooking: true,
    showOnPublicForm: false,
  };
}

const optionCardClassNames = {
  base: cn(
    "!max-w-none m-0 w-full cursor-pointer items-start rounded-lg border-2 border-default-100 bg-content1 p-3",
    "hover:bg-content2 data-[selected=true]:border-primary",
  ),
  wrapper: "hidden",
  labelWrapper: "ms-0",
};

function OptionCard({
  value,
  icon,
  title,
  description,
  identifier,
}: {
  value: string;
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
  identifier: string;
}) {
  return (
    <Radio
      value={value}
      classNames={optionCardClassNames}
      data-identifier={identifier}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-default-500">{icon}</span>
          <span className="font-medium text-sm">{title}</span>
        </div>
        <div className="text-default-500 text-xs">{description}</div>
      </div>
    </Radio>
  );
}

/** The chosen pair in one example, so the two questions read as one decision. */
function CombinationSummary({
  consumesShift,
  granularity,
}: {
  consumesShift: boolean;
  granularity: Form["granularity"];
}) {
  const summary = (() => {
    if (consumesShift && granularity === "DAY") {
      return (
        <Trans>
          <strong>Npr. dopust.</strong> Vnese se kot obdobje, pokrije izmene v
          teh dneh in se odšteje od delovnega časa. Prikaže se v Dopustih.
        </Trans>
      );
    }
    if (consumesShift) {
      return (
        <Trans>
          <strong>Npr. obisk zdravnika, 10:00–12:00.</strong> Vnese se v
          koledarju z uro začetka in konca in se odšteje od delovnega časa.
        </Trans>
      );
    }
    if (granularity === "DAY") {
      return (
        <Trans>
          <strong>Npr. izobraževanje.</strong> Zaposleni ni na voljo ves dan, a
          ti dnevi štejejo v delovni čas. Prikaže se v Dopustih.
        </Trans>
      );
    }
    return (
      <Trans>
        <strong>Npr. malica, 12:00–12:30.</strong> Vnese se v koledarju in šteje
        v delovni čas.
      </Trans>
    );
  })();

  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-default-100 px-3 py-2 text-default-600 text-xs"
      data-identifier="time-off-type-combination-summary"
    >
      <Info size={14} className="mt-0.5 shrink-0 text-default-400" />
      <p>{summary}</p>
    </div>
  );
}

const slotChipClassName =
  "flex h-9 min-w-0 items-center justify-center rounded-lg border border-default-300 px-1.5 text-xs transition-colors";

// Mirrors the public form's SlotChip; booked slots there are greyed the same way.
function PublicFormPreview({
  visibility,
  label,
}: {
  visibility: PublicFormVisibility;
  label: string;
}) {
  return (
    <div aria-hidden className="rounded-lg bg-default-50 p-3">
      <p className="mb-2 text-default-500 text-xs">
        <Trans>Predogled na spletnem obrazcu</Trans>
      </p>
      <div className="grid grid-cols-4 gap-2">
        <span className={slotChipClassName}>10:00</span>
        <span
          className={cn(slotChipClassName, {
            "border-default-200 bg-default-200 text-default-400":
              visibility !== "OPEN",
          })}
        >
          <span className="truncate">
            {visibility === "LABELLED" ? label : "10:30"}
          </span>
        </span>
        <span className={slotChipClassName}>11:00</span>
        <span className={slotChipClassName}>11:30</span>
      </div>
    </div>
  );
}

type PublicFormVisibility = "OPEN" | "BLOCKED" | "LABELLED";

function publicFormVisibility(values: Form): PublicFormVisibility {
  if (!values.blocksOnlineBooking) return "OPEN";
  return values.showOnPublicForm ? "LABELLED" : "BLOCKED";
}

// Only the calendar's break modal reads the default, and it lists TIME types only.
function hasEffectiveDefaultDuration(values: Form): boolean {
  return values.granularity === "TIME" && values.hasDefaultDuration;
}

// A per-day minute budget means nothing on a type entered in whole days.
function hasEffectiveDailyLimit(values: Form): boolean {
  return values.granularity === "TIME" && values.hasDailyLimit;
}

function valuesFromType(type: TimeOffTypeRow): Form {
  return {
    name: type.name ?? "",
    color: type.color,
    emoji: type.emoji ?? fallbackTimeOffEmoji(type.granularity),
    consumesShift: type.consumesShift,
    granularity: type.granularity,
    hasDefaultDuration: type.defaultDurationMins != null,
    defaultDurationMins: type.defaultDurationMins ?? 30,
    hasDailyLimit: type.maxDailyMinutes != null,
    maxDailyMinutes: type.maxDailyMinutes ?? 60,
    blocksOnlineBooking: type.blocksOnlineBooking,
    showOnPublicForm: type.showOnPublicForm,
  };
}

export const ManageTimeOffTypeModal = ({
  isOpen,
  onClose,
  type,
  onSaved,
  fixedGranularity,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** null creates a new type; otherwise edits this one. */
  type: TimeOffTypeRow | null;
  /** Names the created type, so a caller opening this to fill a picker can
   * select what it just made. Absent on an edit. */
  onSaved: (created?: { id: number }) => void;
  /**
   * Pins the duration unit for a caller that only deals in one — the calendar
   * lists time-bounded types only, so a day-long one made there would vanish
   * from the picker the moment it was saved.
   */
  fixedGranularity?: Form["granularity"];
}) => {
  const isEditing = type != null;
  const blankValues = defaultValues(fixedGranularity ?? "DAY");

  const form = useForm<Form>({
    initialValues: type ? valuesFromType(type) : blankValues,
    validate: {
      // A blank name on a seeded type is how you go back to the translated
      // default, so only custom types have to carry one.
      name: (value) =>
        value.trim() || type?.systemKey ? null : t`Vnesite ime`,
      defaultDurationMins: (value, values) =>
        hasEffectiveDefaultDuration(values) && value < 1
          ? t`Trajanje mora biti vsaj 1 minuto`
          : null,
      maxDailyMinutes: (value, values) =>
        hasEffectiveDailyLimit(values) && value < 1
          ? t`Omejitev mora biti vsaj 1 minuto`
          : null,
    },
  });

  const [isGranularityPicked, setIsGranularityPicked] = useState(false);
  const [rateChanged, setRateChanged] = useState(false);
  const canSeeStaffCost = useUserPermissionsStore(
    (state) => state.permissions.view_staff_cost,
  );

  // The list is reloaded once the modal closes, not on each rate save, or the
  // reload would reset the type fields still being edited here.
  function close() {
    if (rateChanged) onSaved();
    onClose();
  }

  function setGranularity(granularity: Form["granularity"]) {
    // A default nobody changed follows the form it was picked for.
    if (form.values.emoji === fallbackTimeOffEmoji(form.values.granularity)) {
      form.setFieldValue("emoji", fallbackTimeOffEmoji(granularity));
    }
    form.setFieldValue("granularity", granularity);
  }

  useEffect(() => {
    if (isOpen) {
      form.setValues(type ? valuesFromType(type) : blankValues);
      setIsGranularityPicked(false);
      setRateChanged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type]);

  const { mutateAsync: postType, isPending: isPosting } =
    api.timeOffType.usePostTimeOffType();
  const { mutateAsync: putType, isPending: isPutting } =
    api.timeOffType.usePutTimeOffType();

  const save = async (applyToFutureInstances: boolean) => {
    const values = form.getValues();
    const body: PostTimeOffType["body"] = {
      name: values.name.trim(),
      color: values.color,
      emoji: values.emoji,
      consumesShift: values.consumesShift,
      granularity: values.granularity,
      defaultDurationMins: hasEffectiveDefaultDuration(values)
        ? values.defaultDurationMins
        : null,
      maxDailyMinutes: hasEffectiveDailyLimit(values)
        ? values.maxDailyMinutes
        : null,
      blocksOnlineBooking: values.blocksOnlineBooking,
      showOnPublicForm: values.showOnPublicForm,
    };

    const created = await (isEditing
      ? putType({
          id: type.id,
          body: {
            ...body,
            isArchived: type.isArchived,
            applyToFutureInstances,
          },
        }).then(() => undefined)
      : postType(body));

    addToast({ title: t`Uspešno shranjeno`, color: "success" });
    onSaved(created);
    onClose();
  };

  const { open: openConsumesShiftPrompt, modal: consumesShiftPromptModal } =
    useConfirmModal({
      title: t`Uporabi za prihodnje vnose?`,
      content: (
        <Trans>
          Spremenili ste, ali je zaposleni v tem času na delu. Želite spremembo
          uveljaviti tudi za že vnesene prihodnje termine te vrste? Pretekli
          vnosi ostanejo nespremenjeni.
        </Trans>
      ),
      confirmProps: {
        label: t`Da, uveljavi tudi za prihodnje`,
        onClick: () => save(true),
      },
      cancelProps: {
        label: t`Ne, samo za nove vnose`,
        // useConfirmModal closes itself after this, and the mutation toasts
        // and reports a failure — this only keeps the rejection from escaping.
        onClick: () => {
          save(false).catch(() => {});
        },
      },
    });

  const handleSubmit = form.onSubmit(async (values) => {
    if (isEditing && values.consumesShift !== type.consumesShift) {
      openConsumesShiftPrompt();
      return;
    }

    try {
      await save(false);
    } catch {
      // The mutation toasts the server's message and reports it to Sentry.
    }
  });

  const isCustomType = type?.systemKey == null;
  // Seeded types can be renamed/recoloured, but their behaviour fields stay
  // locked — changing what a seeded type like VACATION or WALK_IN *does*
  // (rather than what it's called) is a bigger decision than this modal
  // should make casually.
  const isSeededType = type != null && type.systemKey != null;
  const defaultName = type?.systemKey
    ? resolveDefaultTimeOffTypeName(type.systemKey)
    : null;
  const publicLabel = [
    form.values.emoji,
    form.values.name.trim() || defaultName || t`ime vrste`,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <LimeModal isOpen={isOpen} onClose={close} size="xl">
        <ModalContent>
          <form onSubmit={handleSubmit} style={{ display: "contents" }}>
            <ModalHeader>
              {isEditing ? (
                <Trans>Uredi vrsto odsotnosti</Trans>
              ) : (
                <Trans>Dodaj vrsto odsotnosti</Trans>
              )}
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-3">
                {defaultName && (
                  <LimeAlert
                    color="primary"
                    message={t`To je privzeta vrsta (${defaultName}). Če ji spremenite ime, se prevod za druge jezike ne bo več uporabljal — izbrišite ime, da se vrne privzeto.`}
                  />
                )}

                <LimeInput
                  label={t`Ime`}
                  placeholder={defaultName ?? undefined}
                  {...form.getInputProps("name")}
                  endContent={
                    isSeededType && form.values.name.trim() ? (
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        aria-label={t`Uporabi privzeto ime`}
                        onPress={() => form.setFieldValue("name", "")}
                      >
                        <RotateCcw size={16} />
                      </Button>
                    ) : undefined
                  }
                />

                <div className="flex flex-col gap-3 sm:flex-row">
                  <LimeColorPicker
                    label={t`Barva`}
                    wrapperClassName="sm:min-w-0 sm:flex-1"
                    value={form.values.color ?? undefined}
                    onChange={(value) => form.setFieldValue("color", value)}
                  />
                  <EmojiPickerGrid
                    label={t`Emoji`}
                    value={form.values.emoji}
                    fallback={fallbackTimeOffEmoji(form.values.granularity)}
                    onChange={(value) => form.setFieldValue("emoji", value)}
                  />
                </div>

                {/* consumesShift decides work-time subtraction, which types
                    the shifts page offers, and what a shift save may delete. */}
                <LimeRadioGroup
                  label={t`Delovni čas`}
                  classNames={{
                    label: "text-sm text-foreground",
                    wrapper: "grid grid-cols-1 gap-2 sm:grid-cols-2",
                  }}
                  value={form.values.consumesShift ? "ABSENT" : "AT_WORK"}
                  onValueChange={(value) => {
                    const consumesShift = value === "ABSENT";
                    form.setFieldValue("consumesShift", consumesShift);
                    // Most types pair the two, so Oblika follows until it is picked by hand.
                    if (
                      !isEditing &&
                      !isGranularityPicked &&
                      fixedGranularity == null
                    ) {
                      setGranularity(consumesShift ? "DAY" : "TIME");
                    }
                  }}
                  isDisabled={isSeededType}
                >
                  <OptionCard
                    value="ABSENT"
                    identifier="time-off-type-consumes-shift"
                    icon={<TreePalm size={16} />}
                    title={<Trans>Ni na delu</Trans>}
                    description={
                      <Trans>Ta čas se odšteje od delovnega časa.</Trans>
                    }
                  />
                  <OptionCard
                    value="AT_WORK"
                    identifier="time-off-type-at-work"
                    icon={<Coffee size={16} />}
                    title={<Trans>Na delu, a ni na voljo</Trans>}
                    description={
                      <Trans>Šteje v delovni čas, a ni za rezervacije.</Trans>
                    }
                  />
                </LimeRadioGroup>

                <div className="flex flex-col">
                  <LimeRadioGroup
                    label={t`Oblika odsotnosti`}
                    classNames={{
                      label: "text-sm text-foreground",
                      wrapper: "grid grid-cols-1 gap-2 sm:grid-cols-2",
                    }}
                    value={form.values.granularity}
                    onValueChange={(value) => {
                      setIsGranularityPicked(true);
                      setGranularity(value as Form["granularity"]);
                    }}
                    isDisabled={isSeededType || fixedGranularity != null}
                  >
                    <OptionCard
                      value="DAY"
                      identifier="time-off-type-granularity-day"
                      icon={<CalendarRange size={16} />}
                      title={<Trans>Dnevna</Trans>}
                      description={
                        <Trans>
                          Sledi izmenam; prvi in zadnji dan sta lahko krajša.
                        </Trans>
                      }
                    />
                    <OptionCard
                      value="TIME"
                      identifier="time-off-type-granularity-time"
                      icon={<Clock size={16} />}
                      title={<Trans>Urna</Trans>}
                      description={
                        <Trans>
                          Z uro začetka in konca, znotraj enega dne.
                        </Trans>
                      }
                    />
                  </LimeRadioGroup>

                  <div className="pt-2">
                    <CombinationSummary
                      consumesShift={form.values.consumesShift}
                      granularity={form.values.granularity}
                    />
                  </div>

                  <LimeCollapse
                    isOpen={form.values.granularity === "TIME"}
                    className="overflow-hidden"
                  >
                    <div className="pt-2">
                      <div className="flex flex-col rounded-lg bg-default-50 p-3">
                        <Checkbox
                          size="sm"
                          isSelected={form.values.hasDefaultDuration}
                          onValueChange={(value) =>
                            form.setFieldValue("hasDefaultDuration", value)
                          }
                        >
                          <Trans>Privzeto trajanje</Trans>
                        </Checkbox>
                        <LimeCollapse
                          isOpen={form.values.hasDefaultDuration}
                          className="overflow-hidden"
                        >
                          <div className="pt-2">
                            <DurationInput
                              showDays={false}
                              value={form.values.defaultDurationMins}
                              onValueChange={(value) =>
                                form.setFieldValue("defaultDurationMins", value)
                              }
                            />
                          </div>
                        </LimeCollapse>
                        <LimeCollapse
                          isOpen={!!form.errors.defaultDurationMins}
                          className="overflow-hidden"
                        >
                          <p className="pt-2 text-danger text-xs">
                            {form.errors.defaultDurationMins}
                          </p>
                        </LimeCollapse>

                        <Checkbox
                          size="sm"
                          className="mt-2"
                          data-identifier="time-off-type-daily-limit"
                          isSelected={form.values.hasDailyLimit}
                          onValueChange={(value) =>
                            form.setFieldValue("hasDailyLimit", value)
                          }
                        >
                          <Trans>Dnevna omejitev</Trans>
                        </Checkbox>
                        <LimeCollapse
                          isOpen={form.values.hasDailyLimit}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-col gap-1 pt-2">
                            <DurationInput
                              showDays={false}
                              value={form.values.maxDailyMinutes}
                              onValueChange={(value) =>
                                form.setFieldValue("maxDailyMinutes", value)
                              }
                            />
                            <p className="text-default-500 text-xs">
                              <Trans>
                                Največ skupaj na zaposlenega v enem dnevu, za
                                vse vnose te vrste.
                              </Trans>
                            </p>
                          </div>
                        </LimeCollapse>
                        <LimeCollapse
                          isOpen={!!form.errors.maxDailyMinutes}
                          className="overflow-hidden"
                        >
                          <p className="pt-2 text-danger text-xs">
                            {form.errors.maxDailyMinutes}
                          </p>
                        </LimeCollapse>
                      </div>
                    </div>
                  </LimeCollapse>
                </div>

                <div className="flex flex-col">
                  <LimeRadioGroup
                    label={t`Kaj vidijo stranke na spletnem obrazcu?`}
                    description={
                      form.values.granularity === "TIME"
                        ? t`Velja za nove vnose, pri posameznem vnosu v koledarju lahko spremenite.`
                        : undefined
                    }
                    classNames={{
                      label: "text-sm text-foreground",
                      wrapper: "grid grid-cols-1 gap-2 sm:grid-cols-3",
                    }}
                    value={publicFormVisibility(form.values)}
                    onValueChange={(value) =>
                      form.setValues({
                        blocksOnlineBooking: value !== "OPEN",
                        showOnPublicForm: value === "LABELLED",
                      })
                    }
                    isDisabled={isSeededType}
                  >
                    <OptionCard
                      value="OPEN"
                      identifier="time-off-type-public-open"
                      icon={<CalendarCheck size={16} />}
                      title={<Trans>Prost termin</Trans>}
                      description={<Trans>Stranke lahko rezervirajo.</Trans>}
                    />
                    <OptionCard
                      value="BLOCKED"
                      identifier="time-off-type-public-blocked"
                      icon={<CalendarX size={16} />}
                      title={<Trans>Zaseden termin</Trans>}
                      description={<Trans>Brez razloga.</Trans>}
                    />
                    <OptionCard
                      value="LABELLED"
                      identifier="time-off-type-public-labelled"
                      icon={<Tag size={16} />}
                      title={<Trans>Zaseden z oznako</Trans>}
                      description={<Trans>Stranke vidijo ime vrste.</Trans>}
                    />
                  </LimeRadioGroup>

                  <div className="pt-2">
                    <PublicFormPreview
                      visibility={publicFormVisibility(form.values)}
                      label={publicLabel}
                    />
                  </div>

                  <LimeCollapse
                    isOpen={
                      publicFormVisibility(form.values) === "LABELLED" &&
                      isCustomType
                    }
                    className="overflow-hidden"
                  >
                    <div className="pt-2">
                      <LimeAlert
                        color="warning"
                        message={t`Ime te vrste bo vidno vsem strankam, v enem jeziku. Prepričajte se, da je primerno za javno objavo.`}
                      />
                    </div>
                  </LimeCollapse>
                </div>

                {type?.compensation ? (
                  <TimeOffTypeCompensationSection
                    key={type.id}
                    typeId={type.id}
                    initialCompensation={type.compensation}
                    onChanged={() => setRateChanged(true)}
                  />
                ) : canSeeStaffCost && !isEditing ? (
                  <p className="text-default-500 text-xs">
                    <Trans>
                      Nadomestilo za to vrsto nastavite, ko jo shranite.
                    </Trans>
                  </p>
                ) : null}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onPress={close} type="button">
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="primary"
                isLoading={isPosting || isPutting}
                type="submit"
              >
                <Trans>Shrani</Trans>
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </LimeModal>

      {consumesShiftPromptModal}
    </>
  );
};
